<template>
  <li class="timeline-block">
    <span class="timeline-icon" :class="config.color">
      <i class="fa fa-fw" :class="config.icon"></i>
    </span>

    <div class="timeline-content card">
      <div class="card-body">
        <h3 class="timeline-title">{{ event.title }}</h3>
        <p v-if="note" class="timeline-note text-muted">{{ note }}</p>
        <router-link v-if="config.link" :to="config.link" class="btn btn-sm btn-primary">{{ config.linkText }}</router-link>
        <p class="timeline-date-inline small text-muted mb-0 mt-2 d-lg-none">
          {{ event.when | fromNow() }} &middot; {{ event.when | moment('DD/MM/YYYY') }}
        </p>
      </div>
    </div>

    <div class="timeline-date d-none d-lg-block">
      <span class="strong">{{ event.when | fromNow() }}</span><br>
      <small class="text-muted">{{ event.when | moment('DD MMM YYYY') }}</small>
    </div>
  </li>
</template>

<script>
export default {
  props: {
    event: {
      required: true,
      type: Object
    }
  },

  computed: {
    config () {
      const { type, id } = this.event

      return {
        QuoteSent: { icon: 'fa-envelope-square', color: 'bg-info' },
        QuoteAccepted: { icon: 'fa-signature', color: 'bg-success' },
        QuoteVoided: { icon: 'fa-trash-alt', color: 'bg-danger' },
        InvoiceSent: { icon: 'fa-envelope-square', color: 'bg-info', link: { name: 'invoices.show', params: { invoice_id: id } }, linkText: 'View Invoice' },
        InvoicePaid: { icon: 'fa-dollar-sign', color: 'bg-success', link: { name: 'invoices.show', params: { invoice_id: id } }, linkText: 'View Invoice' },
        SLAUsage: { icon: 'fa-paper-plane', color: 'bg-warning', link: { name: 'slas.show', params: { sla_id: id } }, linkText: 'View SLA' }
      }[type] || { icon: 'fa-circle', color: 'bg-secondary' }
    },

    // Invoice notes are invoice line descriptions joined with <br>
    note () {
      return (this.event.note || '').replace(/<br\s*\/?>/g, '\n')
    }
  }
}
</script>

<style scoped>
.timeline-block {
  --timeline-icon-size: 2.5rem;
  --timeline-gap: 1.5rem;

  display: flex;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-left: calc(var(--timeline-icon-size) + var(--timeline-gap));
}

.timeline-icon {
  position: absolute;
  top: 0.75rem;
  left: 0;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--timeline-icon-size);
  height: var(--timeline-icon-size);
  border-radius: 50%;
  color: #fff;
  box-shadow: 0 0 0 4px var(--bs-body-bg, #f6f7f8);
}

.timeline-content {
  position: relative;
  flex: 1 1 auto;
  margin-bottom: 0;
}

/* Arrow pointing at the timeline line */
.timeline-content::before {
  content: '';
  position: absolute;
  top: 1.5rem;
  left: -0.5rem;
  width: 1rem;
  height: 1rem;
  background: inherit;
  transform: rotate(45deg);
}

.timeline-title {
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.timeline-note {
  white-space: pre-line;
  margin-bottom: 0.75rem;
}

.timeline-date {
  padding-top: 1rem;
}

@media (min-width: 992px) {
  .timeline-block {
    padding-left: 0;
  }

  .timeline-icon {
    left: 50%;
    margin-left: calc(var(--timeline-icon-size) / -2);
  }

  /* Content and date each take half, separated by the line and icon */
  .timeline-content,
  .timeline-date {
    flex: 0 0 calc(50% - (var(--timeline-icon-size) / 2) - var(--timeline-gap));
  }

  .timeline-date {
    margin-left: calc(var(--timeline-icon-size) + (var(--timeline-gap) * 2));
  }

  .timeline-content::before {
    left: auto;
    right: -0.5rem;
  }

  /* Alternate sides on every other event */
  .timeline-block:nth-child(even) {
    flex-direction: row-reverse;
  }

  .timeline-block:nth-child(even) .timeline-date {
    margin-left: 0;
    margin-right: calc(var(--timeline-icon-size) + (var(--timeline-gap) * 2));
    text-align: right;
  }

  .timeline-block:nth-child(even) .timeline-content::before {
    left: -0.5rem;
    right: auto;
  }
}
</style>
